import React from 'react';
import { observer } from 'mobx-react';

import Alert from 'react-s-alert';
import { Button, Box, Paragraph } from 'components';

import { CadPreview, makeDecimalIdLink } from 'libs/connection/cells';
import { ProductQuoteApp } from 'apps/productQuote/app';
import { useRouterStore } from 'apps/hooks';
import { WithNamespaces, withNamespaces } from 'react-i18next';
import { RemoveId, ShipmentQuoteNavigate } from 'apps/shipmentQuote/utils';
import { PortalLink } from 'apps/client/components/PortalLink';
import { FullFormField } from 'libs/forms';
import { ProductQuoteBuildDetailsCell } from 'apps/productQuote/cells';
import { formatDate, formatPrice } from 'utils/formatStrings';
import { ShipmentQuoteValidationErrors } from 'apps/shipmentQuote/CreateShipmentQuotePage/ShipmentQuoteValidationErrors';
import { TableSection } from './TableSection';

const onCellClicked = (): void => null;

export const columns = [
  {
    Header: 'Preview',
    accessor: '',
    Cell: function preview(pq: any) {
      // In the 2 column (Preview, Part Name) we get the file from the 'for' property
      // we might also want to get the file from the 'from.jobSpec' property e.g. for greater user recognition
      // In practice, the 'from.jobSpec' property is the same as the 'for' property as the build file is not
      // changed during the quoting process (for now). So we use the for property to avoid overfetching.
      const { file } = pq.for.build;
      return (
        <PortalLink
          name='rfq'
          params={{ rfqId: pq.from.id }}
          onClick={onCellClicked}
        >
          {file ? CadPreview(file) : <p>Cannot get preview...</p>}
        </PortalLink>
      );
    },
  },
  {
    Header: 'RFQ ID',
    accessor: 'from.id',
    Cell: function rfqId(id: string) {
      return makeDecimalIdLink({
        routeName: 'rfq',
        paramName: 'rfqId',
        prefix: 'R',
      })(id);
    },
  },
  {
    Header: 'Part Name',
    accessor: 'for.build.file',
    Cell: function PartName(file: any) {
      return file.filename;
    },
  },
  {
    Header: 'Part Details',
    accessor: '',
    Cell: ProductQuoteBuildDetailsCell,
  },
  {
    Header: 'Unit Price',
    // isNumeric: true,
    accessor: '',
    Cell: function unitCost(productQuote: ProductQuoteApp) {
      if (productQuote.id) {
        return formatPrice(
          productQuote.unitCost / 100.0,
          productQuote.currency,
        );
      }

      const outString = '--';
      if (!productQuote.from.isQuotable) {
        return <>{outString}</>;
      }

      const refs = {};
      return (
        <FullFormField
          fieldStore={productQuote.form.fieldStores.get('unitCost')}
          refs={refs}
        />
      );
    },
  },
  {
    Header: 'Quantity',
    // isNumeric: true,
    accessor: '',
    Cell: function quantityCell(productQuote: ProductQuoteApp) {
      if (productQuote.id) {
        return productQuote.quantity;
      }

      const outString = '--';
      if (!productQuote.from.isQuotable) {
        return <>{outString}</>;
      }

      const refs = {};

      return (
        <FullFormField
          fieldStore={productQuote.form.fieldStores.get('quantity')}
          refs={refs}
        />
      );
    },
  },
  {
    Header: 'Price W/O fee',
    accessor: '',
    component: true,
    Cell: observer(function totalCostCell({
      data: productQuote,
    }: {
      data: ProductQuoteApp;
    }) {
      let outString = '--';
      const isFilledLocalProductQuote = productQuote.cost !== '';
      if (productQuote.id || isFilledLocalProductQuote) {
        outString = formatPrice(
          productQuote.cost / 100.0,
          productQuote.currency,
        );
      }
      return <>{outString}</>;
    }),
  },
  {
    Header: 'Supplier',
    accessor: '',
    Cell: function supplierCell(productQuote: ProductQuoteApp) {
      const prefix = productQuote.isInstantQuote ? '(IQ) ' : '';
      return `${prefix}${productQuote.supplier?.companyName || '-'}`;
    },
  },
  {
    Header: 'Valid Until',
    accessor: 'validUntil',
    Cell: function ValidUntil(date: Date) {
      if (date) {
        return <Paragraph>{formatDate(date)}</Paragraph>;
      }
      return <Paragraph>-</Paragraph>;
    },
  },
];

const getFinalCostColumn = (shipmentQuoteApp: any) => {
  return {
    Header: 'Final Price',
    accessor: '',
    component: true,
    Cell: observer(function finalCostCell({
      data: productQuote,
    }: {
      data: ProductQuoteApp;
    }) {
      let outString = '--';
      const isFilledLocalProductQuote =
        productQuote.cost !== '' &&
        typeof shipmentQuoteApp.takeRate === 'number';
      if (productQuote.id || isFilledLocalProductQuote) {
        if (productQuote.currency !== shipmentQuoteApp.currency) {
          outString = 'Programming Error Currency mismatch'; // Let's try and make this never happen
        }
        const final = productQuote.cost / (1 - shipmentQuoteApp.takeRate / 100);
        outString = formatPrice(final / 100.0, productQuote.currency);
      }
      return <>{outString}</>;
    }),
  };
};

const getRowActionsButtons = (appManager: any) => {
  const RowActionButtons = ({ node }: { node: ProductQuoteApp }) => {
    const routerStore = useRouterStore();
    const handleButtonClick = () => {
      let errored = false;
      if (!appManager.shipmentQuoteApp.currency) {
        Alert.error('Please select a currency first.');
        errored = true;
      }
      if (!appManager.shipmentQuoteApp.manufacturer) {
        Alert.error('Please select a manufacturer first.');
        errored = true;
      }
      if (!errored) appManager.SelectProductQuote(node);
    };
    const handleRemoveClick = () => {
      if (node.id) {
        // it's a real product quote
        ShipmentQuoteNavigate(routerStore, {
          pqids: RemoveId(routerStore, 'pqids', node),
        });
      } else {
        // it's a fake product quote created by the frontend from an RFQ
        ShipmentQuoteNavigate(routerStore, {
          rfqids: RemoveId(routerStore, 'rfqids', node.from),
        });
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          variant='destructive'
          size='small'
          outline
          icon='delete'
          margin='0 8px 0 0'
          onClick={handleRemoveClick}
        />
        <Button
          size='small'
          text='Open'
          outline
          onClick={handleButtonClick}
          icon='arrow_right'
          iconPosition='right'
        />
      </Box>
    );
  };
  return RowActionButtons;
};

interface ShipmentProductQuotesProps extends WithNamespaces {
  shipmentQuoteAppManager: any;
}

const ShipmentProductQuotesObs = observer(function ShipmentProductQuotes({
  shipmentQuoteAppManager,
  t,
}: ShipmentProductQuotesProps) {
  const { productQuoteApps, shipmentQuoteApp } = shipmentQuoteAppManager; // RFQApps
  const loading =
    shipmentQuoteAppManager?.rfqsQuery?.loading ||
    shipmentQuoteAppManager?.productQuotesQ.loading;
  const handleClick = () => {
    shipmentQuoteAppManager.OpenEditPartsModal();
  };

  const getHoveredComponent = (node: any) => {
    if (!node.id && node.from.isQuotable === false) {
      return (
        <span>
          This RFQ is not quotable, please remove it or choose another one.
        </span>
      );
    }
    return null;
  };

  const button = (
    <Button
      size='small'
      icon='add'
      text='Edit Parts'
      variant='primary'
      outline
      onClick={handleClick}
    />
  );
  const finalColumns = columns.slice();
  finalColumns.splice(columns.length, 0, getFinalCostColumn(shipmentQuoteApp));

  return (
    <TableSection
      loading={loading}
      columns={finalColumns}
      nodes={productQuoteApps}
      title={t('Part Breakdown')}
      dataTestId='shipmentQuotePage-product-quotes-table'
      shouldRowBeGreyedOut={node => !node.id && node.from.isQuotable === false}
      getHoveredComponent={getHoveredComponent}
      RowActionButtons={getRowActionsButtons(shipmentQuoteAppManager)}
      emptyTablePlaceholder={null}
      Button={button}
      warningMessage={
        <ShipmentQuoteValidationErrors
          shipmentQuoteAppManager={shipmentQuoteAppManager}
        />
      }
    />
  );
});
//
export const ShipmentProductQuotesTable = withNamespaces()(
  ShipmentProductQuotesObs,
);
